

import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { Compartment, CompartmentChangeStateEnum } from '@/entities/compartments/models/Compartment'
import { useDropdown } from '@/entities/dropdowns/libs/useDropdown'
import { planningShiftsSelectors, runsheetShiftsSelectors } from '@/entities/schedules'
import { saveShift } from '@/entities/schedules/thunks/saveShift'
import { Button, Modal } from '@/shared/components'
import { DialogComponentProps } from '@/shared/components/Modal/models/DialogStructure'
import { timeDayjsToHhMm, timeHhMmSsToDayjs } from '@/shared/lib/timeHelper'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { Box, styled, Tab, Tabs } from '@mui/material'

import { defaultShiftValues } from './defaultShiftValues'
import { ShiftFormValues } from './models/ShiftFormValues'
import { CompartmentsTab } from './tabs/CompartmentsTab'
import { CostsTab } from './tabs/CostsTab'
import { FatigueManagementTab } from './tabs/FatigueManagementTab'
import { GeneralTab } from './tabs/GeneralTab'
import { RecordsTab } from './tabs/RecordsTab'
import { WorkingHoursTab } from './tabs/WorkingHoursTab'

export type EditShiftDialogProps = DialogComponentProps & {
  onClose: () => void
}

export const EditShiftDialog: React.FC<EditShiftDialogProps> = ({
  open,
  onClose,
  formType,
  shiftId,
}) => {
  const [activeTab, setActiveTab] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const currentScreen = useAppSelector((state) => state.userInterface.currentScreen)
  const shift = useAppSelector((state) => {
    if (currentScreen === 'planning') {
      return planningShiftsSelectors.selectById(state, shiftId)
    }

    return runsheetShiftsSelectors.selectById(state, shiftId)
  })

  const dispatch = useAppDispatch()
  const isReadOnly = formType === 'view'

  // Fetch dropdowns
  const { data: depotOptions } = useDropdown('depots')
  const { data: runCategoryOptions } = useDropdown('runCategories')
  const { data: colorOptions } = useDropdown('htmlColors')
  const { data: attributeOptions, completed: attrCompleted } = useDropdown('vehicleAttributes')
  const { data: compartmentOptions, completed: compartmentCompleted } = useDropdown('compartments')

  const methods = useForm<ShiftFormValues>({
    defaultValues: defaultShiftValues,
  })

  const { reset, handleSubmit } = methods

  const resetForm = () => {
    if (!shift || !attrCompleted || !attrCompleted) return

    reset({
      ...shift,
      startDepotId: depotOptions.find(opt => opt.value === shift.startDepotId) || null,
      finishDepotId: depotOptions.find(opt => opt.value === shift.finishDepotId) || null,
      uiColorId: colorOptions.find(opt => opt.value === shift.uiColor) || null,
      runCategoryId: runCategoryOptions.find(opt => opt.value === shift.runCategoryId) || null,
      attributeIds: shift.attributeIds.map(id => attributeOptions.find(opt => opt.value === id)) || [],
      compartmentIds: shift.compartments.map(compartment => compartmentOptions.find(opt => opt.value === compartment.compartmentDefinitionId)) || [],

      loadTime: timeHhMmSsToDayjs(shift.loadTime),
      unloadTime: timeHhMmSsToDayjs(shift.unloadTime),
      overtimeKickIn1: timeHhMmSsToDayjs(shift.overtimeKickIn1),
      overtimeKickIn2: timeHhMmSsToDayjs(shift.overtimeKickIn2),
      breakDuration: timeHhMmSsToDayjs(shift.breakDuration || '00:00'),
      isPaidBreaks: shift.isPaidBreaks || false,

      workDay: {
        ...shift.workDay,
        startTimeEarliest: timeHhMmSsToDayjs(shift.workDay.startTimeEarliest),
        startTimeLatest: timeHhMmSsToDayjs(shift.workDay.startTimeLatest),
        finishTimeLatest: timeHhMmSsToDayjs(shift.workDay.finishTimeLatest),
        maxDrivingTime: timeHhMmSsToDayjs(shift.workDay.maxDrivingTime),
        maxWorkingTime: timeHhMmSsToDayjs(shift.workDay.maxWorkingTime),
        loadTimeEarliest: timeHhMmSsToDayjs(shift.workDay.loadTimeEarliest),
        loadTimeLatest: timeHhMmSsToDayjs(shift.workDay.loadTimeLatest),
        minPaidTime: timeHhMmSsToDayjs(shift.workDay.minPaidTime),
      },
    })
  }

  useEffect(() => {
    if (!shift || !['edit', 'view'].includes(formType) || !attrCompleted || !compartmentCompleted) return

    resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shift, formType, attrCompleted, compartmentCompleted])


  const onSubmit = async (data: ShiftFormValues) => {
    try {
      setIsSubmitting(true)

      // Map compartmentDefinitions back to the format required by the API
      const compartments: Partial<Compartment>[] = shift?.compartments.map((compartment) => {
        const match = data.compartmentIds.find((opt) => opt.value === compartment.compartmentDefinitionId)

        // If there is a match, the compartment is unchanged
        if (match) {
          return {
            id: match.value,
            compartmentDefinitionId: match.compartmentDefinitionId,
            state: CompartmentChangeStateEnum.Unchanged,
          }
        }

        return {
          id: compartment.id,
          compartmentDefinitionId: compartment.compartmentDefinitionId,
          state: CompartmentChangeStateEnum.Deleted,
        }
      }) || []

      data.compartmentIds.forEach((opt) => {
        const match = shift?.compartments.find((compartment) => compartment.compartmentDefinitionId === opt.value)

        if (!match) {
          compartments.push({
            id: 0,
            compartmentDefinitionId: opt.value,
            state: CompartmentChangeStateEnum.Added,
          })
        }
      })

      const submitData = {
        ...data,
        startDepotId: data.startDepotId?.value,
        finishDepotId: data.finishDepotId?.value,
        uiColorId: data.uiColorId?.value,
        runCategoryId: data.runCategoryId?.value,
        attributeIds: data.attributeIds.map(opt => opt.value),
        compartments: compartments,

        loadTime: timeDayjsToHhMm(data.loadTime),
        unloadTime: timeDayjsToHhMm(data.unloadTime),
        overtimeKickIn1: timeDayjsToHhMm(data.overtimeKickIn1),
        overtimeKickIn2: timeDayjsToHhMm(data.overtimeKickIn2),
        breakDuration: timeDayjsToHhMm(data.breakDuration),

        workDay: {
          ...shift.workDay,
          startTimeEarliest: timeDayjsToHhMm(data.workDay.startTimeEarliest),
          startTimeLatest: timeDayjsToHhMm(data.workDay.startTimeLatest),
          finishTimeLatest: timeDayjsToHhMm(data.workDay.finishTimeLatest),
          maxDrivingTime: timeDayjsToHhMm(data.workDay.maxDrivingTime),
          maxWorkingTime: timeDayjsToHhMm(data.workDay.maxWorkingTime),
          loadTimeEarliest: timeDayjsToHhMm(data.workDay.loadTimeEarliest),
          loadTimeLatest: timeDayjsToHhMm(data.workDay.loadTimeLatest),
          minPaidTime: timeDayjsToHhMm(data.workDay.minPaidTime),
        },
      }

      await dispatch(saveShift({ shift: submitData })).unwrap()
      onClose?.()
    } catch (error) {
      // Handle error - maybe show toast
      console.error('Failed to save shift:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      title={`${formType === 'view' ? 'View' : 'Edit'} Run: ${shift?.name}`}
      onClose={onClose}
      open={open}
      transition="opacity"
      showBackground={true}
    >
      <StyledContent>
        <FormProvider {...methods}>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
              <Tab label="General" disableRipple />
              <Tab label="Costs" disableRipple />
              <Tab label="Working Hours" disableRipple />
              <Tab label="Fatigue Management" disableRipple />
              <Tab label="Compartments" disableRipple />
              {currentScreen === 'runsheet' && <Tab label="Records" disableRipple />}
            </Tabs>

            <StyledTabPanel display={activeTab === 0 ? 'block' : 'none'}>
              <GeneralTab
                isReadOnly={isReadOnly}
                depotOptions={depotOptions}
                attributeOptions={attributeOptions}
              />
            </StyledTabPanel>

            <StyledTabPanel display={activeTab === 1 ? 'block' : 'none'}>
              <CostsTab
                isReadOnly={isReadOnly}
              />
            </StyledTabPanel>

            <StyledTabPanel display={activeTab === 2 ? 'block' : 'none'}>
              <WorkingHoursTab
                isReadOnly={isReadOnly}
              />
            </StyledTabPanel>

            <StyledTabPanel display={activeTab === 3 ? 'block' : 'none'}>
              <FatigueManagementTab
                isReadOnly={isReadOnly}
                standardFatigueSettings={{
                  breakDuration: timeHhMmSsToDayjs('00:15'),
                  fatigueModels: [
                    { id: 1, numberOfBreaks: 1, timeInterval: '05:30', state: 1 },
                    { id: 2, numberOfBreaks: 2, timeInterval: '08:00', state: 1 },
                    { id: 3, numberOfBreaks: 4, timeInterval: '11:00', state: 1 },
                  ],
                }}
              />
            </StyledTabPanel>

            <StyledTabPanel display={activeTab === 4 ? 'block' : 'none'}>
              <CompartmentsTab
                isReadOnly={isReadOnly}
                compartmentOptions={compartmentOptions}
                attributeOptions={attributeOptions}
              />
            </StyledTabPanel>

            {currentScreen === 'runsheet' && <StyledTabPanel display={activeTab === 5 ? 'block' : 'none'}>
              <RecordsTab />
            </StyledTabPanel>}

            {formType !== 'view' && <StyledFormActions>
              <Button text onClick={onClose} disabled={isSubmitting}>Cancel</Button>
              <Button primary type="submit" disabled={isSubmitting}>
                Submit
              </Button>
            </StyledFormActions>}
          </form>
        </FormProvider>
      </StyledContent>
    </Modal>
  )
}

const StyledContent = styled('div')`
  ${(props) => props.theme.breakpoints.up('sm')} {
    width: ${(props) => props.theme.typography.pxToRem(720)};
    height: 80vh;
  }
`

const StyledFormActions = styled('div')`
  position: sticky;
  bottom: ${(props) => props.theme.typography.pxToRem(-12)};
  left: 0;
  right: 0;
  z-index: 1001;
  width: 100%;
  padding-bottom: ${(props) => props.theme.typography.pxToRem(12)};
  display: flex;
  justify-content: flex-end;
  gap: ${(props) => props.theme.typography.pxToRem(12)};
  margin-top: ${(props) => props.theme.typography.pxToRem(24)};
  padding-top: ${(props) => props.theme.typography.pxToRem(8)};
  background-color: ${(props) => props.theme.palette.background.paper};
`

const StyledTabPanel = styled(Box)`
  width: 100%;
  height: calc(100% - ${(props) => props.theme.typography.pxToRem(109)});
  position: relative;
  overflow-y: auto;
  scrollbar-gutter: stable;
`